import EditRoundedIcon from "@mui/icons-material/EditRounded";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { assetKeys } from "../../../../../api/queryKeys";
import CustomSwitch from "../../../../../components/CustomSwitch";
import CustomToolTipWithPermission from "../../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../../components/dialogs/ConfirmationDialog";
import { Toast } from "../../../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import {
  ASSET_CARD_SIZE,
  ManualSteps,
  PageDisplayMode,
} from "../../../../../utils/constants";
import CreateNewAssetModal from "../wizard/CreateNewAssetModal";
import AssetActionMenu from "./AssetActionMenu";
import ContentAssetCard from "./ContentAssetCard";

const SmartAssetCard = ({
  asset,
  selected,
  onClick,
  hasPermission,
  displayMode,
}) => {
  const colors = useColors();
  const { updateSmartAsset, trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const navigate = useNavigate();
  const { businessValueMode } = useContext(EnvFeatureFlagContext);
  const handleNewScenarioView = (assetId) => {
    navigate("/posture/surface/", {
      state: { filterCriticalAsset: assetId },
    });
  };

  const { mutate: updateIsActive, isPending } = useMutation({
    mutationFn: updateSmartAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.all });
      if (!asset.is_active)
        Toast.info({
          text: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2">{`Smart Critical Asset turned on
         `}</Typography>
              <Typography variant="subtitle2" color={colors.text_secondary}>
                {asset.name}
              </Typography>

              {asset.scenario_sets_count > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" color={colors.text_secondary}>
                    {asset.scenario_sets_count +
                      " " +
                      pluralize("attack scenario", asset.scenario_sets_count) +
                      " identified."}
                  </Typography>
                  <Button
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: colors.primary_8percent,
                      color: colors.primary_main,
                      textTransform: "none",
                      width: "150px",
                    }}
                    onClick={() => handleNewScenarioView(asset.asset_id)}
                  >
                    <Typography variant="body2" color={colors.text_secondary}>
                      {"View " +
                        pluralize("Attack Scenario", asset.scenario_sets_count)}
                    </Typography>
                  </Button>
                </Box>
              ) : (
                ""
              )}
            </Box>
          ),
        });
      else
        Toast.info({
          text: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2">{`Smart Critical Asset turned off
         `}</Typography>
              <Typography variant="subtitle2" color={colors.text_secondary}>
                {asset.name}
              </Typography>
              <Typography variant="body2" color={colors.text_secondary}>
                {asset.scenario_sets_count > 0
                  ? asset.scenario_sets_count +
                    " " +
                    pluralize("attack scenario", asset.scenario_sets_count) +
                    " removed."
                  : ""}
              </Typography>
            </Box>
          ),
        });
    },
  });

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    trackEvent(
      `User closed the modal to update an asset, from the assets page`
    );
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEditModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent(`User is opening update modal for asset ${asset.name}`);
    setOpenEditModal(true);
  };

  const handleSwitchChange = (event) => {
    event.stopPropagation();
    if (!asset.is_active) {
      updateIsActive({
        asset_id: asset.asset_id,
        is_active: !asset.is_active,
      });
    } else {
      trackEvent(
        `User is opening disable modal for asset ${asset.name} and removing attack scenarios`
      );
      setOpenDialog(true);
    }
  };
  const DisplayCardStyle = {
    width: ASSET_CARD_SIZE.WIDTH_CARD,
    height: ASSET_CARD_SIZE.HEIGHT_CARD,
  };
  const DisplayTableStyle = {
    width: ASSET_CARD_SIZE.WIDTH,
    height: ASSET_CARD_SIZE.HEIGHT,
  };
  return (
    <Box
      position="relative"
      display={"flex"}
      flexDirection={displayMode === PageDisplayMode.CARD ? "row" : "column"}
      justifyContent={"space-between"}
      onClick={!openEditModal && !openDialog ? onClick : () => {}}
      mt={1}
      sx={{
        ...(displayMode === PageDisplayMode.CARD
          ? DisplayCardStyle
          : DisplayTableStyle),
        borderRadius: "16px",
        padding: 3,
        cursor: "pointer",
        gap: "12px",
        justifyContent: "space-between",
        border: !asset.is_active
          ? "1.5px dashed " + colors.primary_32percent
          : "none",
        boxShadow: selected
          ? "0px 0px 6px 1px " + colors.primary_100 + " inset"
          : asset.is_active
          ? "0px 0px 5.3px 0px " + colors.primary_32percent
          : "none",

        "&:hover": {
          boxShadow: asset.is_active
            ? "0px 0px 4px 1px " + colors.primary_50
            : "0px 0px 4px 1px " + colors.primary_32percent + " !important",
        },
      }}
    >
      {asset?.new_entity_ids?.length > 0 && (
        <Box
          position="absolute"
          left="40%"
          top="-12px"
          transform="translateX(-50%)"
          px={1}
          borderRadius="8px"
          bgcolor={colors.common_white}
          color={colors.primary_main}
          boxShadow={"0px 0px 5.3px 0px " + colors.primary_32percent}
          fontSize="14px"
          fontWeight="medium"
          display="flex"
          alignItems="center"
          padding="3px"
          zIndex={100}
        >
          <Typography
            variant="caption"
            color={colors.primary_main}
            padding="0px 4px"
          >
            {"Updated"}
          </Typography>
        </Box>
      )}
      <ContentAssetCard
        asset={asset}
        assetId={asset?.asset_id}
        activeAsset={asset?.is_active}
        assetType={asset?.type}
        assetName={asset?.name}
        numEntityIds={asset?.entity_ids?.length}
        scenarioSetsCount={asset?.scenario_sets_count}
        displayMode={displayMode}
      >
        {displayMode === PageDisplayMode.CARD && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              gap: "4px",
              alignItems: "flex-end",
            }}
          >
            <AssetActionMenu
              handleOpenUpdateModal={businessValueMode ? handleEditModal : null}
              handleSwitchChange={handleSwitchChange}
              isActive={asset.is_active}
              editLabel="Edit business value"
              hasPermission={hasPermission}
            />
          </Box>
        )}
      </ContentAssetCard>

      {displayMode !== PageDisplayMode.CARD && (
        <>
          <Divider />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {businessValueMode ? (
              <CustomToolTipWithPermission
                title="Edit business value"
                permission={hasPermission}
                placement="top"
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: "6px",
                    padding: "4px",
                    border: "1px solid " + colors.grey_300,
                    "&:hover": {
                      backgroundColor: colors.grey_200,
                    },
                  }}
                  onClick={businessValueMode ? handleEditModal : null}
                >
                  <EditRoundedIcon
                    sx={{
                      color: colors.action_active,
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Box>
              </CustomToolTipWithPermission>
            ) : (
              <Box />
            )}

            <CustomToolTipWithPermission
              title={
                asset.is_active
                  ? "This Smart Critical Asset is active. Turn it off to remove related attack scenarios."
                  : "This Smart Critical Asset is disabled. Turn it on for Breeze to identify and add related attack scenarios."
              }
              permission={hasPermission}
              placement="top"
            >
              <FormControlLabel // need to add form control label to make the switch work properly
                sx={{ margin: "0px", alignItems: "baseline" }}
                onClick={(e) => e.stopPropagation()} // Prevents the card from being clicked when the switch is clicked
                disabled={!hasPermission}
                control={
                  <CustomSwitch
                    checked={asset.is_active}
                    onChange={hasPermission ? handleSwitchChange : null}
                    disabled={isPending}
                  />
                }
                label=""
              />
            </CustomToolTipWithPermission>
          </Box>
        </>
      )}
      <CreateNewAssetModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        assetToEdit={{ asset: asset, entities: [] }}
        steps={ManualSteps}
      />

      <ConfirmationDialog
        openDialog={openDialog}
        handleDialogClose={handleCloseDialog}
        handleConfirm={() => {
          trackEvent(`User is disabling an asset, from the assets page`);
          if (asset.is_active) {
            updateIsActive({
              asset_id: asset.asset_id,
              is_active: !asset.is_active,
            });
            setOpenDialog(false);
          }
        }}
        dialogTitle={`Disable Asset and Remove Attack Scenarios`}
        dialogContentFirstLine="Disabling this Smart Critical Asset will remove all attack scenarios identified by Breeze for this asset. You can re-enable it at any time."
        dialogContentSecondLine="Are you sure you want to proceed?"
        confirmationButtonText="Disable"
        cancelButtonText="Cancel"
        destructive={true}
      />
    </Box>
  );
};

export default SmartAssetCard;
